import { isEmptyIncludeCheckDateRange } from 'utils/empty';
import { Ticket } from './Ticket';
import { Moment } from 'moment';
import moment from 'utils/app-moment';
import {
  dropdownInboxesSelector,
  loadDataErrorInboxesSelector,
  loadingInboxesSelector,
} from 'store/selectors/inboxes.selector';
import {
  dropdownAssigneesSelector,
  loadDataErrorAssigneesSelector,
  loadingAssigneesSelector,
} from 'store/selectors/assignees.selector';
import {
  dropdownCountriesSelector,
  loadDataErrorCountriesSelector,
  loadingCountriesSelector,
} from 'store/selectors/countries.selector';
import { EachWorkdeskTicketI } from 'store/models/workdesk-tickets';
import Booking from 'store/models/bookings/Booking';

const advanceFilterItems = [
  {
    name: 'ticketIds', // -> store as ticketIds
    label: 'Ticket ID',
    type: 'MULTI_TEXT', // auto split?
  },
  {
    name: 'subject',
    label: 'Subject',
    type: 'TEXT',
  },
  {
    name: 'bookingNos', // -> store as bookingNos
    label: 'Booking No.',
    type: 'MULTI_TEXT', // auto split?
  },
  {
    // data from /users ?
    name: 'assigneeIds', // -> store as assigneeIds
    label: 'Assignee',
    type: 'MULTI_SELECT', // should be multi select instead
    loadingSelector: loadingAssigneesSelector,
    dataSelector: dropdownAssigneesSelector,
    loadDataErrorSelector: loadDataErrorAssigneesSelector,
  },
  {
    // data from /inboxes (same as in the nav bar)
    name: 'inboxes', // -> store as inboxes or should the field just be called with the s already?
    label: 'Inbox',
    type: 'MULTI_SELECT', // should be multi select instead
    loadingSelector: loadingInboxesSelector,
    dataSelector: dropdownInboxesSelector,
    loadDataErrorSelector: loadDataErrorInboxesSelector,
  },
  {
    name: 'labels',
    label: 'Labels',
    type: 'MULTI_TEXT',
  },
  {
    name: 'updateDate',
    label: 'Last Update',
    type: 'DATE_RANGE',
  },
  {
    name: 'receivedTime',
    label: 'Received Time',
    type: 'DATE_RANGE',
  },
  {
    name: 'submitBookingDateInitial',
    label: 'Sent (Initial)',
    type: 'DATE_RANGE',
  },

  {
    name: 'submitBookingDate',
    label: 'Sent (Latest)',
    type: 'DATE_RANGE',
  },
  {
    name: 'senderIds',
    label: 'Indexer ID',
    type: 'MULTI_TEXT',
  },
  {
    name: 'countries',
    label: 'Countries',
    type: 'MULTI_SELECT',
    loadingSelector: loadingCountriesSelector,
    dataSelector: dropdownCountriesSelector,
    loadDataErrorSelector: loadDataErrorCountriesSelector,
  },
] as {
  name: string;
  label: string;
  type: string;
  defaultValue?: any;
}[];

export interface SearchValues {
  ticketIds?: string;
  subject?: string;
  updateDate?: { startDate: Moment | null; endDate: Moment | null };
  receivedTime?: { startDate: Moment | null; endDate: Moment | null };
  // and other fields
}

export interface SearchValuesApi {
  ticketIds?: string[];
  subject?: string;
  updateDate?: { start: string; end: string };
  receivedTime?: { start: string; end: string };
  // and other fields
}

const SEARCH_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const fieldLabelMap = advanceFilterItems.reduce(
  (acc, field) => ({ ...acc, [field.name]: field.label }),
  {},
);
/**
 * TODO: decide if want to use class or just wrap functions in const
 */
class SearchFilter {
  static hasSearchValuesChanged(
    cur: SearchValuesApi,
    prev?: SearchValuesApi,
  ) {
    return JSON.stringify(cur) !== JSON.stringify(prev);
  }
  /**
   * is subject by default
   * is bookingNos if match booking no regex (if one match booking no, it will all be booking nos)
   * is ticketIds is match ticketIds regex (has 16 digits)
   * @param value
   */
  static searchValueIsField = (
    value: string,
  ): 'subject' | 'bookingNos' | 'ticketIds' => {
    if (Booking.isBookingNo(value)) {
      return 'bookingNos';
    } else if (Ticket.isTicketId(value)) {
      return 'ticketIds';
    }
    return 'subject';
  };

  static getFieldLabel = (field: string) => {
    return fieldLabelMap[field];
  };

  static fieldLabelMap = fieldLabelMap;
  static advanceFilterItems = advanceFilterItems;
  static specialSearchFields = ['labels'];
  static dateRangeFields = advanceFilterItems
    .filter((item) => item.type === 'DATE_RANGE')
    .map((item) => item.name);
  static multiTextOrMultiSelectFields = advanceFilterItems
    .filter((item) =>
      ['MULTI_TEXT', 'MULTI_SELECT'].includes(item.type),
    )
    .map((item) => item.name);

  static getAdvanceSearchFilters() {
    return SearchFilter.advanceFilterItems;
  }
  static getAdvanceFilterNames() {
    return SearchFilter.advanceFilterItems.map((item) => item.name);
  }
  static getInitialAdvanceFilterValue() {
    return SearchFilter.advanceFilterItems.reduce(
      (acc, item) => ({
        ...acc,
        [item.name]: SearchFilter.getDefaultValueForSearchKey(
          item.name,
        ),
      }),
      {},
    );
  }

  static getDefaultValueForSearchKey = (searchKey: string) => {
    if (SearchFilter.isDateRangeSearchValueKey(searchKey)) {
      return {
        startDate: null,
        endDate: null,
      };
    } else if (SearchFilter.isMultiTextSearchValueKey(searchKey)) {
      return [];
    } else return '';
  };

  static hasAnySearchValues(
    searchValues?: SearchValues | SearchValuesApi,
  ): boolean {
    if (!searchValues) return false;
    return (
      Object.values(searchValues).length > 0 &&
      Object.values(searchValues).some(
        (val) => !isEmptyIncludeCheckDateRange(val),
      )
    );
  }
  static hasNonSubjectSearchValues(
    searchValues: SearchValues | SearchValuesApi,
  ) {
    if (!searchValues) return false;
    return (
      Object.values(searchValues).length > 0 &&
      Object.entries(searchValues).some(
        ([key, val]) =>
          key !== 'subject' && !isEmptyIncludeCheckDateRange(val),
      )
    );
  }
  /**
   * format searchValues from the store to api
   * like parsed comma ticketId => ticketIds
   */
  static storeStateToApi(
    storeSearchValues: SearchValuesApi,
  ): undefined | SearchValuesApi {
    if (!storeSearchValues) return storeSearchValues;
    if (!SearchFilter.hasAnySearchValues(storeSearchValues))
      return undefined;
    let result = { ...storeSearchValues };

    Object.keys(result).forEach((key) => {
      const val = result[key];
      if (SearchFilter.isDateRangeSearchValueKey(key)) {
        result[key] = SearchFilter.getApiDateValuesFromStoreDate(val);
      }
    });

    return result as SearchValuesApi;
  }

  static tmptoStoreState(
    tmpSearchValues: SearchValues,
  ): SearchValuesApi {
    if (!tmpSearchValues) {
      return {};
    }
    // remove all the empty
    const ori: SearchValues = Object.entries(tmpSearchValues).reduce(
      (acc, [name, value]) =>
        !isEmptyIncludeCheckDateRange(value)
          ? { ...acc, [name]: value }
          : acc,
      {},
    );

    let result = { ...ori };

    Object.keys(result).forEach((key) => {
      const val = result[key];
      if (SearchFilter.isDateRangeSearchValueKey(key)) {
        result[key] = SearchFilter.getStoreDateValuesFromTmpDate(val);
      }
    });

    return result as SearchValuesApi;
  }

  static getApiDateValuesFromStoreDate(storeDate?: {
    start: string;
    end: string;
  }) {
    return {
      start:
        (storeDate?.start &&
          moment(storeDate?.start)
            .utc()
            .format(SEARCH_DATE_FORMAT)) ||
        '',
      end:
        (storeDate?.end &&
          moment(storeDate?.end).utc().format(SEARCH_DATE_FORMAT)) ||
        '',
    };
  }

  static getStoreDateValuesFromTmpDate(tmpDate?: {
    startDate: Moment | null;
    endDate: Moment | null;
  }) {
    return {
      start:
        (tmpDate?.startDate &&
          moment(
            tmpDate?.startDate?.format('YYYY-MM-DD 00:00:01'),
          ).format(SEARCH_DATE_FORMAT)) ||
        '',
      end:
        (tmpDate?.endDate &&
          moment(
            tmpDate?.endDate?.format('YYYY-MM-DD 23:59:59'),
          ).format(SEARCH_DATE_FORMAT)) ||
        '',
    };
  }

  /**
   *
   * @param storeDate
   */
  static getTmpDateValuesFromStoreDate(storeDate?: {
    start: string;
    end: string;
  }) {
    const tmpDate = {
      startDate: storeDate?.start ? moment(storeDate?.start) : null,
      endDate: storeDate?.end ? moment(storeDate?.end) : null,
    };
    console.debug(`tmpDate: `, tmpDate);
    return tmpDate;
  }

  /**
   * store to tmp
   * @param apiValues
   */
  static apiToSearchValues(apiValues: SearchValuesApi): SearchValues {
    if (!apiValues) return apiValues;

    let result = { ...apiValues } as SearchValues;

    Object.keys(apiValues).forEach((key) => {
      const val = apiValues[key];
      if (SearchFilter.isDateRangeSearchValueKey(key)) {
        result[key] = SearchFilter.getTmpDateValuesFromStoreDate(val);
      }
    });

    return result;
  }

  static storeStateToSearchValues(
    apiValues: SearchValuesApi,
  ): SearchValues {
    if (!apiValues) return {};
    return SearchFilter.apiToSearchValues(apiValues);
  }

  static isDateRangeSearchValueKey(searchValueKey: string) {
    return SearchFilter.dateRangeFields.includes(searchValueKey);
  }

  static isMultiTextSearchValueKey(searchValueKey: string) {
    return SearchFilter.multiTextOrMultiSelectFields.includes(
      searchValueKey,
    );
  }

  static isDateInRangeOfStoreDate(
    ticketDate?: string,
    storeDate?: { start: string; end: string },
  ): boolean {
    if (!storeDate || isEmptyIncludeCheckDateRange(storeDate))
      return true;
    else if (!ticketDate) return false;
    else {
      if (!storeDate.start && storeDate.end) {
        return moment(ticketDate).isSameOrBefore(storeDate.end);
      } else if (storeDate.start && !storeDate.end) {
        return moment(ticketDate).isSameOrAfter(storeDate.start);
      } else {
        return moment(ticketDate).isBetween(
          storeDate.start,
          storeDate.end,
          null,
          '[]',
        );
      }
    }
  }
  static searchValueApiToTicketApiKeyMap = {
    ticketIds: 'id',
    assigneeIds: 'assigneeId',
    inboxes: 'inboxAddress',
    senderIds: 'senderId',
    countries: 'country',
  };

  static getTicketApiKeyForSearchValue(searchValueKey: string) {
    return (
      SearchFilter.searchValueApiToTicketApiKeyMap[searchValueKey] ||
      searchValueKey
    );
  }

  static isTicketMatchedStoreStateSearchValues(
    ticket: EachWorkdeskTicketI,
    searchValues?: SearchValuesApi,
  ): boolean {
    if (!ticket) return false;
    if (!searchValues) return true;
    if (!SearchFilter.hasAnySearchValues(searchValues)) return true;
    // use OR
    return Object.entries(searchValues).every(([name, value]) => {
      if (isEmptyIncludeCheckDateRange(value)) return true;
      let matched = false;
      const ticketKey = SearchFilter.getTicketApiKeyForSearchValue(
        name,
      );
      if (ticketKey && ticketKey in ticket) {
        const ticketVal = ticket[ticketKey];
        if (name === 'labels' && value.length) {
          if (!ticket.labels?.length) {
            matched = false;
          } else {
            matched = ticket.labels.some((item) =>
              value.some((searchLabel) =>
                item.name
                  .toLowerCase()
                  .includes(searchLabel.toLowerCase()),
              ),
            );
          }
        } else if (name === 'bookingNos') {
          // search in both ticket's bookingNos and bookingNo
          value = value.map((v) => `${v}`.toLowerCase());
          matched =
            (ticket.bookingNos &&
              ticket.bookingNos.some(
                (bn) => bn && value.includes(bn.toLowerCase()),
              )) ||
            (ticket.bookingNo &&
              value.includes(ticket.bookingNo.toLowerCase()));
        } else if (SearchFilter.isDateRangeSearchValueKey(name)) {
          // date range field
          matched = SearchFilter.isDateInRangeOfStoreDate(
            ticketVal,
            value,
          );
        } else {
          if (Array.isArray(value)) {
            // array
            // console.debug(`value: `, value, ticketVal, ticket);
            matched = value
              .map((v) => `${v}`.toLowerCase())
              .includes(`${ticketVal}`.toLowerCase());
          } else {
            // text
            matched = `${ticketVal}`
              .toLowerCase()
              .includes(`${value}`.toLowerCase());
          }
        }
      } else if (ticketKey && !(ticketKey in ticket)) {
        // if ticket doesn't have ticketKey -> just matched
        matched = true;
      }
      return matched;
    });
  }
  static splitString(value: string): string[] {
    return value
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v !== '');
  }
  static joinStrings(value: string[]): string {
    return value.join(',');
  }
}

export default SearchFilter;
